import React, { useState } from 'react';
import Header from './components/Header';
import Login from './components/Login';
import { Routes, Route, useNavigate, useParams, useLocation, Navigate } from 'react-router-dom';
import { isTokenValid, installAuthInterceptor } from './utils/authSession';
import FirmsPage from './components/FirmsPage';
import FirmDetailPage from './components/FirmDetailPage';
import PipelineBoard from './components/PipelineBoard';
import CandidateList from './components/CandidateList';
import ClientLogin from './components/ClientLogin';
import ClientDashboard from './components/ClientDashboard';
import ClientCandidateArchive from './components/ClientCandidateArchive';
import ClientPipelineView from './components/ClientPipelineView';
import ClientCandidateProfile from './components/ClientCandidateProfile';
import UserManagement from './components/UserManagement';
import UserManagementPage from './components/UserManagementPage';
import UserProfile from './components/UserProfile';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import MessagingProgram from './components/MessagingProgram';
import LandingPage from './components/LandingPage';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import CandidateListExpanded from './components/CandidateListExpanded';
import CandidateProfileExpanded from './components/CandidateProfileExpanded';
import ClientTagsPage from './components/ClientTagsPage';
import SearchesPage from './components/SearchesPage';
import SearchDetailPage from './components/SearchDetailPage';
import OutreachPage from './components/OutreachPage';
import CampaignDetailPage from './components/CampaignDetailPage';
import FileViewer from './components/FileViewer';
import SharedFileViewer from './components/SharedFileViewer';
import HandshakesPage from './components/HandshakesPage';
import Dashboard from './components/Dashboard';
import AssistantLauncher from './components/AssistantLauncher';
import SmsConsent from './components/SmsConsent';
import TalentNetwork from './components/TalentNetwork';
import AvailabilityPicker from './components/AvailabilityPicker';

// Install global 401 interceptor
installAuthInterceptor();

// Auth guard wrapper
function HomeRoute() {
    if (isTokenValid()) return <Dashboard />;
    return <LandingPage />;
}

function RequireAuth({ children }) {
    if (!isTokenValid()) return <Navigate to="/login" replace />;
    return children;
}

function RequireClientAuth({ children }) {
    if (!isTokenValid()) return <Navigate to="/client/login" replace />;
    return children;
}

// Helper component to pass URL param as prop
function PipelineBoardWrapper() {
    const { pipelineId } = useParams();
    return <PipelineBoard pipelineId={parseInt(pipelineId)} />;
}

// AssistantLauncher is rendered as a sibling to Routes so its panel
// exists across all routes. It's a fixed-position floating panel — it
// doesn't push content around when open. Only shows when authenticated;
// the launcher itself checks visibility based on token.
function App() {
    return (
        <>
            <Routes>
            {/* Public routes — no auth required */}
            <Route path="/login" element={<Login onLoginSuccess={() => window.location.href = '/'} />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/client/login" element={<ClientLogin />} />
            <Route path="/client/forgot-password" element={<ForgotPassword />} />
            <Route path="/client/reset-password" element={<ResetPassword />} />

            {/* Internal protected routes */}
            <Route path="/" element={<HomeRoute />} />
            <Route path="/firms" element={<RequireAuth><FirmsPage /></RequireAuth>} />
            <Route path="/firm/:id" element={<RequireAuth><FirmDetailPage /></RequireAuth>} />
            <Route path="/pipeline/:pipelineId" element={<RequireAuth><PipelineBoardWrapper /></RequireAuth>} />
            <Route path="/candidates" element={<RequireAuth><CandidateListExpanded /></RequireAuth>} />
            <Route path="/candidates-expanded" element={<RequireAuth><CandidateListExpanded /></RequireAuth>} />
            <Route path="/candidate/:candidateId" element={<RequireAuth><CandidateProfileExpanded /></RequireAuth>} />
            <Route path="/searches" element={<RequireAuth><SearchesPage /></RequireAuth>} />
            <Route path="/search/:id" element={<RequireAuth><SearchDetailPage /></RequireAuth>} />
            <Route path="/outreach" element={<RequireAuth><OutreachPage /></RequireAuth>} />
            <Route path="/outreach/:id" element={<RequireAuth><CampaignDetailPage /></RequireAuth>} />
            <Route path="/file/:fileId" element={<RequireAuth><FileViewer /></RequireAuth>} />
            <Route path="/s/:token" element={<SharedFileViewer />} />
            <Route path="/pipelines" element={<RequireAuth><Dashboard /></RequireAuth>} />
            <Route path="/opportunities" element={<RequireAuth><HandshakesPage /></RequireAuth>} />
            <Route path="/users" element={<RequireAuth><UserManagement /></RequireAuth>} />
            <Route path="/user-management" element={<RequireAuth><UserManagementPage /></RequireAuth>} />
            <Route path="/profile" element={<RequireAuth><UserProfile /></RequireAuth>} />

            {/* Client protected routes */}
            <Route path="/client/dashboard" element={<RequireClientAuth><ClientDashboard /></RequireClientAuth>} />
            <Route path="/client/archive" element={<RequireClientAuth><ClientCandidateArchive /></RequireClientAuth>} />
            <Route path="/client/tags" element={<RequireClientAuth><ClientTagsPage /></RequireClientAuth>} />
            <Route path="/client/candidate/:candidateId" element={<RequireClientAuth><ClientCandidateProfile /></RequireClientAuth>} />
            <Route path="/client/pipeline/:pipelineId" element={<RequireClientAuth><ClientPipelineView /></RequireClientAuth>} />
            <Route path="/client/profile" element={<RequireClientAuth><UserProfile /></RequireClientAuth>} />
            <Route path="/client/user-management" element={<RequireClientAuth><UserManagementPage /></RequireClientAuth>} />

            {/* Public pages */}
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            {/* Public SMS/messaging program page — cited from Terms §6 and
                from the A2P 10DLC campaign message_flow. TCR reviewers
                navigate here to verify the consent + opt-out workflow, so
                it must be reachable without a session. */}
            <Route path="/messaging" element={<MessagingProgram />} />
            {/* SMS consent — public URL for A2P 10DLC compliance.
                Cited in the Twilio campaign submission's message_flow
                as the reviewer-testable consent capture page. Must be
                reachable without a session — no RequireAuth. */}
            <Route path="/sms-consent" element={<SmsConsent />} />
            {/* Tokenized consent flow — recruiters generate a unique
                link per candidate via SmsConsentManager and share it
                over email/LinkedIn. SmsConsent detects the :token
                param via useParams and switches to the tokenized
                sub-flow (fetches candidate context, submits to
                /api/sms/consent/:token/grant). Same component, one
                file, mode chosen at render time. */}
            <Route path="/sms-consent/:token" element={<SmsConsent />} />
            {/* Talent Network — public candidate-acquisition page and the
                A2P-compliant "organic web opt-in" surface. The join action
                completes with name + email alone; SMS is an optional
                checkbox. Reachable without a session — no RequireAuth. */}
            <Route path="/talent-network" element={<TalentNetwork />} />
            {/* Interview availability — public tokenized page. A recruiter
                generates the link from the candidate's Interviews panel; the
                candidate opens it (no login) and selects times. */}
            <Route path="/availability/:token" element={<AvailabilityPicker />} />

            {/* Catch-all */}
            <Route path="/client/*" element={<Navigate to="/client/login" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <AssistantLauncher />
        </>
    );
}

export default App;